// REACH-S1 (docs/design/2026-10-28-agent-reach.md §2 "Who can talk to it"): a person outside an
// agent's Hears from is refused visibly and can ask for access in one click. Three places use it:
//   - a mention's wake line (`not_in_reach`): "Ask for access" beside the reason, for the author;
//   - opening a DM with the agent (403 `not_in_reach`): the error toast carries "Ask for access";
//   - the inbox row of the people asked: Grant / Decline (`AccessRequestActions`).
// The request goes to the people the agent serves (else the company's owners and admins); asking
// again returns the pending ask, so a double click never sends two.
import { ApiError } from "@companyos/sdk";
import { Button, InfoTip, toast } from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { CheckIcon, KeyRoundIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";

type AskResult = { to: { id: string; name: string }[] };

const askedText = (r: AskResult) =>
  r.to.length ? `Asked ${r.to.map((t) => t.name).join(", ")}.` : "Asked the company's admins.";

/** "Ask for access" to one agent; once sent, says whom it went to. */
export function AskAccessButton({
  api,
  co,
  agentId,
  agentName,
}: {
  api: Api;
  co: string;
  agentId: string;
  agentName: string;
}) {
  const ask = useMutation({
    mutationFn: () =>
      api.client.agentReach.requestAccess({ params: { co, id: agentId }, body: {} }),
  });
  if (ask.isSuccess)
    return (
      <span data-slot="access-asked" className="text-xs text-muted-foreground">
        {askedText(ask.data)}
      </span>
    );
  const error =
    ask.error instanceof ApiError && ask.error.code === "conflict"
      ? "You can already talk to it: ask again."
      : ask.error
        ? "Could not send the request."
        : null;
  return (
    <span className="inline-flex items-center gap-1">
      <Button
        size="xs"
        variant="secondary"
        disabled={ask.isPending}
        aria-label={`Ask for access to ${agentName}`}
        onClick={() => ask.mutate()}
      >
        <KeyRoundIcon data-icon="inline-start" aria-hidden="true" />
        Ask for access
      </Button>
      <InfoTip label="About asking for access">
        {agentName} only takes asks from the people it serves and those above it. This sends them a
        request; once granted, ask again.
      </InfoTip>
      {error ? (
        <span role="alert" className="text-xs text-danger-text">
          {error}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A refused DM open (403 `not_in_reach`) as a toast whose action asks for access. True when the
 * error was that refusal (the caller shows nothing else), false otherwise.
 */
export function reachRefusalToast(api: Api, co: string, error: unknown): boolean {
  if (!(error instanceof ApiError) || error.code !== "not_in_reach") return false;
  const p = error.problem as { detail?: string; agent_membership_id?: string } | null;
  const agentId = p?.agent_membership_id;
  toast.add({
    title: "Needs access",
    description: p?.detail ?? "This agent only takes asks from the people it serves.",
    type: "error",
    ...(agentId
      ? {
          actionProps: {
            children: "Ask for access",
            onClick: () =>
              void api.client.agentReach
                .requestAccess({ params: { co, id: agentId }, body: {} })
                .then((r) => toast.add({ title: askedText(r), type: "success" }))
                .catch(() => toast.add({ title: "Could not send the request.", type: "error" })),
          },
        }
      : {}),
  });
  return true;
}

/** Grant / Decline of an access request (the inbox row of the people asked). */
export function AccessRequestActions({
  api,
  co,
  requestId,
  onDecided,
}: {
  api: Api;
  co: string;
  requestId: string;
  onDecided?: (state: "granted" | "declined") => void;
}) {
  const [done, setDone] = useState<"granted" | "declined" | null>(null);
  const decide = useMutation({
    mutationFn: (how: "grant" | "decline") =>
      how === "grant"
        ? api.client.agentReachRequests.grant({ params: { co, id: requestId } })
        : api.client.agentReachRequests.decline({ params: { co, id: requestId } }),
    onSuccess: (r) => {
      const state = r.state === "granted" ? "granted" : "declined";
      setDone(state);
      onDecided?.(state);
    },
  });
  if (done)
    return (
      <span className="text-xs text-muted-foreground">
        {done === "granted" ? "Granted" : "Declined"}
      </span>
    );
  const settled = decide.error instanceof ApiError && decide.error.code === "conflict";
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Button
        size="xs"
        variant="secondary"
        disabled={decide.isPending}
        onClick={() => decide.mutate("grant")}
      >
        <CheckIcon data-icon="inline-start" aria-hidden="true" />
        Grant
      </Button>
      <Button
        size="xs"
        variant="ghost"
        disabled={decide.isPending}
        onClick={() => decide.mutate("decline")}
      >
        <XIcon data-icon="inline-start" aria-hidden="true" />
        Decline
      </Button>
      <InfoTip label="About access requests">
        Grant adds them to who the agent hears from; they can then mention or message it. Decline
        changes nothing.
      </InfoTip>
      {decide.error ? (
        <span role="alert" className="text-xs text-danger-text">
          {settled ? "Already decided." : "That did not work."}
        </span>
      ) : null}
    </span>
  );
}
